import {
  AlertCircle,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  HelpCircle,
  Lightbulb,
  MessageCircle,
  MessageSquare,
  Plus,
  Send,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { supportCaseApi } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import {
  CaseCategory,
  CaseStatus,
  CreateSupportCaseRequest,
  SupportCase,
  SupportCaseWithConversations
} from '../types/support-case';
import SupportCaseForm from './SupportCaseForm';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { useToast } from './ui/use-toast';

interface SupportCasesPageProps {
  isActive: boolean;
}

const SupportCasesPage: React.FC<SupportCasesPageProps> = ({ isActive }) => {
  const { toast } = useToast();
  const [supportCases, setSupportCases] = useState<SupportCase[]>([]);
  const [filteredCases, setFilteredCases] = useState<SupportCase[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCaseDetails, setShowCaseDetails] = useState(false);
  const [selectedCase, setSelectedCase] = useState<SupportCaseWithConversations | null>(null);
  const [isCreatingCase, setIsCreatingCase] = useState(false);
  const [isUpdatingCase, setIsUpdatingCase] = useState(false);
  const [isAddingMessage, setIsAddingMessage] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [includeClosed, setIncludeClosed] = useState(false);

  // Form states
  const [newMessage, setNewMessage] = useState('');
  const [showClosedCases, setShowClosedCases] = useState(false);

  useEffect(() => {
    if (isActive) {
      loadSupportCases();
    }
  }, [isActive, includeClosed]);

  useEffect(() => {
    filterCases();
  }, [supportCases, searchTerm, statusFilter, categoryFilter]);

  const loadSupportCases = async () => {
    setIsLoading(true);
    try {
      const response = await supportCaseApi.getUserSupportCases(includeClosed);
      if (response.success && response.data) {
        setSupportCases(response.data.supportCases);
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to load support cases",
          status: "error",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load support cases",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterCases = () => {
    let filtered = [...supportCases];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(case_ => 
        case_.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(case_ => case_.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(case_ => case_.caseCategory === categoryFilter);
    }

    setFilteredCases(filtered);
  };

  const handleCreateCase = async (data: CreateSupportCaseRequest) => {
    setIsCreatingCase(true);
    try {
      const response = await supportCaseApi.createSupportCase(data);
      if (response.success && response.data) {
        await loadSupportCases();
        toast({
          title: "Success",
          description: "Support case created successfully",
        });
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create support case",
        status: "error",
      });
    } finally {
      setIsCreatingCase(false);
    }
  };

  const handleCloseCase = async (caseId: string) => {
    setIsUpdatingCase(true);
    try {
      const response = await supportCaseApi.updateSupportCase(caseId, { status: 'closed' });
      if (response.success && response.data) {
        await loadSupportCases();
        toast({
          title: "Success",
          description: "Support case closed successfully",
        });
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to close support case",
        status: "error",
      });
    } finally {
      setIsUpdatingCase(false);
    }
  };

  const handleViewCaseDetails = async (case_: SupportCase) => {
    try {
      const response = await supportCaseApi.getSupportCaseWithConversations(case_.id);
      if (response.success && response.data) {
        setSelectedCase(response.data.supportCase);
        setShowCaseDetails(true);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load case details",
        status: "error",
      });
    }
  };

  const handleAddMessage = async () => {
    if (!selectedCase || !newMessage.trim()) return;

    setIsAddingMessage(true);
    try {
      const response = await supportCaseApi.addCaseConversation(selectedCase.id, {
        message: newMessage.trim()
      });
      if (response.success && response.data) {
        // Reload case details
        const updatedResponse = await supportCaseApi.getSupportCaseWithConversations(selectedCase.id);
        if (updatedResponse.success && updatedResponse.data) {
          setSelectedCase(updatedResponse.data.supportCase);
        }
        setNewMessage('');
        toast({
          title: "Success",
          description: "Message added successfully",
        });
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add message",
        status: "error",
      });
    } finally {
      setIsAddingMessage(false);
    }
  };

  const getCategoryIcon = (category: CaseCategory) => {
    switch (category) {
      case 'issue':
        return <AlertCircle className="w-4 h-4" />;
      case 'question':
        return <HelpCircle className="w-4 h-4" />;
      case 'feature_request':
        return <Lightbulb className="w-4 h-4" />;
      case 'feedback':
        return <MessageCircle className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getCategoryLabel = (category: CaseCategory): string => {
    switch (category) {
      case 'issue':
        return 'Issue';
      case 'question':
        return 'Question';
      case 'feature_request':
        return 'Feature Request';
      case 'feedback':
        return 'Feedback';
      default:
        return category;
    }
  };

  const getStatusIcon = (status: CaseStatus) => {
    switch (status) {
      case 'new':
        return <Clock className="w-4 h-4" />;
      case 'in_progress':
        return <AlertCircle className="w-4 h-4" />;
      case 'closed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: CaseStatus) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: CaseStatus): string => {
    switch (status) {
      case 'new':
        return 'New';
      case 'in_progress':
        return 'In Progress';
      case 'closed':
        return 'Closed';
      default:
        return status;
    }
  };

  if (!isActive) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Support Cases</h2>
          <p className="text-gray-600">Manage your support requests and get help</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create Case
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="search">Search</Label>
              <Input
                id="search"
                placeholder="Search cases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="issue">Issue</SelectItem>
                  <SelectItem value="question">Question</SelectItem>
                  <SelectItem value="feature_request">Feature Request</SelectItem>
                  <SelectItem value="feedback">Feedback</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => setIncludeClosed(!includeClosed)}
                className="w-full"
              >
                {includeClosed ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                {includeClosed ? 'Hide Closed' : 'Show Closed'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cases Table */}
      <Card>
        <CardHeader>
          <CardTitle>Your Support Cases ({filteredCases.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading support cases...</p>
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No support cases found</p>
              <Button 
                onClick={() => setShowCreateForm(true)} 
                className="mt-4"
                variant="outline"
              >
                Create your first case
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Category</th>
                    <th className="text-left py-3 px-4 font-medium">Title</th>
                    <th className="text-left py-3 px-4 font-medium">Status</th>
                    <th className="text-left py-3 px-4 font-medium">Created</th>
                    <th className="text-left py-3 px-4 font-medium">Updated</th>
                    <th className="text-right py-3 px-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCases.map((case_) => (
                    <tr key={case_.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(case_.caseCategory)}
                          <span className="text-sm">{getCategoryLabel(case_.caseCategory)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="max-w-xs">
                          <p className="font-medium truncate">{case_.title}</p>
                          <p className="text-sm text-gray-600 truncate">{case_.description}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={getStatusColor(case_.status)}>
                          <div className="flex items-center gap-1">
                            {getStatusIcon(case_.status)}
                            {getStatusLabel(case_.status)}
                          </div>
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDateTime(case_.createdAt)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDateTime(case_.updatedAt)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewCaseDetails(case_)}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                          {case_.status !== 'closed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCloseCase(case_.id)}
                              disabled={isUpdatingCase}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Case Form */}
      <SupportCaseForm
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSubmit={handleCreateCase}
        isLoading={isCreatingCase}
      />

      {/* Case Details Dialog */}
      <Dialog open={showCaseDetails} onOpenChange={setShowCaseDetails}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Support Case Details</DialogTitle>
          </DialogHeader>
          
          {selectedCase && (
            <div className="space-y-6">
              {/* Case Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {getCategoryIcon(selectedCase.caseCategory)}
                  <Badge className={getStatusColor(selectedCase.status)}>
                    {getStatusLabel(selectedCase.status)}
                  </Badge>
                  <span className="text-sm text-gray-600">
                    {getCategoryLabel(selectedCase.caseCategory)}
                  </span>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold">{selectedCase.title}</h3>
                  <p className="text-gray-600 mt-2">{selectedCase.description}</p>
                </div>
                
                <div className="text-sm text-gray-500">
                  <p>Created: {formatDateTime(selectedCase.createdAt)}</p>
                  <p>Updated: {formatDateTime(selectedCase.updatedAt)}</p>
                </div>
              </div>

              {/* Conversations */}
              <div className="space-y-4">
                <h4 className="font-semibold">Conversation</h4>
                
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {selectedCase.conversations.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No messages yet</p>
                  ) : (
                    selectedCase.conversations.map((conversation) => (
                      <div
                        key={conversation.id}
                        className={`p-4 rounded-lg ${
                          conversation.respondedBy
                            ? 'bg-blue-50 border-l-4 border-blue-400'
                            : 'bg-gray-50 border-l-4 border-gray-400'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium">
                            {conversation.respondedBy ? 'Support Team' : 'You'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDateTime(conversation.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm">{conversation.message}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Message */}
                {selectedCase.status !== 'closed' && (
                  <div className="space-y-2">
                    <Label htmlFor="newMessage">Add a message</Label>
                    <div className="flex gap-2">
                      <Textarea
                        id="newMessage"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        rows={3}
                        className="flex-1"
                      />
                      <Button
                        onClick={handleAddMessage}
                        disabled={!newMessage.trim() || isAddingMessage}
                        className="self-end"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCaseDetails(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportCasesPage; 